import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Progress } from '../ui/progress';
import { InfoIcon, TrendingUp, TrendingDown, Minus, Download, X } from 'lucide-react';
import * as analyticsApi from '../../api/analytics';

// Range Picker Component
export function RangePicker({ value, onChange }) {
  const ranges = [
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
  ];

  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
      {ranges.map((range) => (
        <button
          key={range.value}
          onClick={() => onChange(range.value)}
          className={`
            px-4 py-2 text-sm font-medium rounded-md transition-all
            ${value === range.value
              ? 'bg-blue-600 text-white shadow-sm'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }
          `}
        >
          {range.label}
        </button>
      ))}
    </div>
  );
}

// KPI Tile Component
export function KpiTile({ title, value, delta, tooltip, format = 'number', suffix = '', loading = false }) {
  const formatValue = (val) => {
    if (val === null || val === undefined) return '—';
    if (format === 'currency') return `$${(val / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    if (format === 'percent') return `${(val * 100).toFixed(1)}%`;
    if (format === 'number') return val.toLocaleString();
    return val;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
            <div className="h-8 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-sm font-medium text-gray-600">{title}</p>
              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <InfoIcon className="h-3.5 w-3.5 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs text-xs">{tooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatValue(value)}
              {suffix && <span className="text-sm text-gray-500 ml-1">{suffix}</span>}
            </p>
          </div>
          {delta !== undefined && delta !== null && (
            <div className={`flex items-center text-sm font-medium ${
              delta > 0 ? 'text-green-600' : delta < 0 ? 'text-red-600' : 'text-gray-500'
            }`}>
              {delta > 0 && <TrendingUp className="h-4 w-4 mr-1" />}
              {delta < 0 && <TrendingDown className="h-4 w-4 mr-1" />}
              {delta === 0 && <Minus className="h-4 w-4 mr-1" />}
              {delta > 0 ? '+' : ''}{delta}%
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Metric Help Popover
export function MetricHelp({ title, description }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger>
          <InfoIcon className="h-4 w-4 text-gray-400" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          <p className="font-semibold mb-1">{title}</p>
          <p className="text-xs text-gray-600">{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Status Badge Component
export function StatusBadge({ type, children }) {
  const variants = {
    LOW_CTR: 'destructive',
    AD_OPPORTUNITY_LOST_BUDGET: 'warning',
    SLOW_REPLIES: 'warning',
    ON_TIME_RISK: 'destructive',
    DISPUTE_SPIKE: 'destructive',
    AD_LOST_SHARE_BUDGET: 'warning',
    AD_LOST_SHARE_RANK: 'warning',
    AD_INEFFICIENT: 'warning',
    AD_LOW_COVERAGE: 'secondary',
  };

  return (
    <Badge variant={variants[type] || 'secondary'} className="text-xs">
      {children}
    </Badge>
  );
}

// Placement Legend (Top vs Mid)
export function PlacementLegend() {
  return (
    <div className="flex items-center gap-4 text-sm">
      <div className="flex items-center gap-2">
        <div className="h-3 w-3 rounded-sm bg-blue-600"></div>
        <span className="text-gray-600">Top</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-3 w-3 rounded-sm bg-purple-600"></div>
        <span className="text-gray-600">Mid</span>
      </div>
    </div>
  );
}

// Impression Share Progress Bar
export function ImpressionShareBar({ label, value, lostBudget, lostRank, placement = 'top' }) {
  const color = placement === 'top' ? 'bg-blue-600' : 'bg-purple-600';
  const lostBudgetColor = 'bg-orange-400';
  const lostRankColor = 'bg-gray-400';

  const wonPercent = value * 100;
  const lostBudgetPercent = lostBudget * 100;
  const lostRankPercent = lostRank * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-gray-700">{label}</span>
        <span className="text-gray-600">{wonPercent.toFixed(1)}%</span>
      </div>
      <div className="h-6 w-full bg-gray-100 rounded-full overflow-hidden flex">
        {wonPercent > 0 && (
          <div 
            className={`${color} flex items-center justify-center text-xs text-white font-medium`}
            style={{ width: `${wonPercent}%` }}
            aria-label={`Won: ${wonPercent.toFixed(1)}%`}
          >
            {wonPercent >= 10 && `${wonPercent.toFixed(0)}%`}
          </div>
        )}
        {lostBudgetPercent > 0 && (
          <div 
            className={`${lostBudgetColor} flex items-center justify-center text-xs text-white font-medium`}
            style={{ width: `${lostBudgetPercent}%` }}
            aria-label={`Lost (Budget): ${lostBudgetPercent.toFixed(1)}%`}
          >
            {lostBudgetPercent >= 10 && `${lostBudgetPercent.toFixed(0)}%`}
          </div>
        )}
        {lostRankPercent > 0 && (
          <div 
            className={`${lostRankColor} flex items-center justify-center text-xs text-white font-medium`}
            style={{ width: `${lostRankPercent}%` }}
            aria-label={`Lost (Rank): ${lostRankPercent.toFixed(1)}%`}
          >
            {lostRankPercent >= 10 && `${lostRankPercent.toFixed(0)}%`}
          </div>
        )}
      </div>
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span>Won: {wonPercent.toFixed(1)}%</span>
        {lostBudgetPercent > 0 && <span className="text-orange-600">Budget: {lostBudgetPercent.toFixed(1)}%</span>}
        {lostRankPercent > 0 && <span className="text-gray-600">Rank: {lostRankPercent.toFixed(1)}%</span>}
      </div>
    </div>
  );
}

// Coverage Hours Heat Strip
export function CoverageHoursStrip({ hours = [] }) {
  // hours is an array of hour indices (0-23) that had activity
  const allHours = Array.from({ length: 24 }, (_, i) => i);
  
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700">Coverage Hours ({hours.length}/24)</p>
      <div className="grid grid-cols-24 gap-0.5">
        {allHours.map((hour) => {
          const hasActivity = hours.includes(hour);
          return (
            <TooltipProvider key={hour}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`
                      h-6 rounded-sm transition-colors
                      ${hasActivity ? 'bg-blue-600' : 'bg-gray-200'}
                    `}
                    aria-label={`Hour ${hour}: ${hasActivity ? 'Active' : 'Inactive'}`}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{hour}:00 - {hasActivity ? 'Active' : 'No activity'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">Each square represents an hour (0-23)</p>
    </div>
  );
}

// Download Button
export function DownloadButton({ type, range, children }) {
  const handleDownload = () => {
    const url = analyticsApi.getExportUrl(type, range);
    window.open(url, '_blank');
  };

  return (
    <Button onClick={handleDownload} variant="outline" className="gap-2">
      <Download className="h-4 w-4" />
      {children}
    </Button>
  );
}

// Dismiss Nudge Button
export function DismissNudgeButton({ nudgeKey, onDismiss, size = 'sm' }) {
  return (
    <Button 
      onClick={() => onDismiss(nudgeKey)} 
      variant="ghost" 
      size={size}
      className="gap-1"
    >
      <X className="h-3.5 w-3.5" />
      Dismiss
    </Button>
  );
}

// Empty State
export function EmptyState({ title, description, action }) {
  return (
    <Card>
      <CardContent className="p-12 text-center">
        <div className="mx-auto max-w-md">
          <div className="mb-4 text-gray-400">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-4">{description}</p>
          {action}
        </div>
      </CardContent>
    </Card>
  );
}

// Error State
export function ErrorState({ error, onRetry }) {
  return (
    <Card className="border-red-200">
      <CardContent className="p-12 text-center">
        <div className="mx-auto max-w-md">
          <div className="mb-4 text-red-400">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load data</h3>
          <p className="text-sm text-gray-600 mb-4">{error?.message || 'An error occurred'}</p>
          {onRetry && (
            <Button onClick={onRetry} variant="outline">
              Try Again
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Loading Skeleton for KPI Grid
export function KpiGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-8 bg-gray-200 rounded w-2/3"></div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
